import { useState, useMemo, useCallback } from "react";

const BASE = "https://support.gs-group.fr/api";
const hdr = () => ({ "Content-Type":"application/json", Authorization:"Bearer "+localStorage.getItem("nexo_token") });

const JOURS  = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];
const MOIS   = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const HEURES = Array.from({length:13},(_,i)=>i+7);

// ── Palette par NATURE de l'événement / statut RH ──
const NATURE_PALETTE = {
  installation: { bg:"#e0f2fe", bd:"#7dd3fc", tx:"#075985", lb:"Installation"    },
  fdi:          { bg:"#fef3c7", bd:"#fcd34d", tx:"#92400e", lb:"FDI"             },
  sav:          { bg:"#f3e8ff", bd:"#d8b4fe", tx:"#6b21a8", lb:"SAV"             },
  absence:      { bg:"#991b1b", bd:"#450a0a", tx:"#ffffff", lb:"Absence injustifiée" },
  maladie:      { bg:"#991b1b", bd:"#450a0a", tx:"#ffffff", lb:"Maladie"         },
  conges:       { bg:"#d1fae5", bd:"#6ee7b7", tx:"#065f46", lb:"Congés/RTT"     },
  rtt:          { bg:"#d1fae5", bd:"#6ee7b7", tx:"#065f46", lb:"RTT"             },
};

// Correspondance pack → emoji (type_pack nouveau champ + modele_pv existant BDD)
const PACK_EMOJI = {
  flash_controle:  "🚨",
  sentinelle:      "🎥",
  sav_classique:   "🛠️",
  cash_bosch:      "💰",
  defi_care:       "❤️",
  defibrillateur:  "⚡",
  // valeurs modele_pv déjà en BDD (fallback sans migration)
  pack_flash:      "🚨",
  pack_sentinelle: "🎥",
  pack_sav:        "🛠️",
  pack_cash_box:   "💰",
  maintenance:     "🔧",
};

// Détecte la palette à partir de nature_evenement (explicite) ou type_intervention
function getNaturePalette(int) {
  const nat = (int.nature_evenement || "").toLowerCase().trim();
  if (NATURE_PALETTE[nat]) return NATURE_PALETTE[nat];

  const type = (int.type_intervention || int.type || "").toLowerCase();
  if (type.includes("install"))                              return NATURE_PALETTE.installation;
  if (type.includes("fdi") || type.includes("deplacement")) return NATURE_PALETTE.fdi;
  if (type.includes("absence"))                             return NATURE_PALETTE.absence;
  if (type.includes("maladie"))                             return NATURE_PALETTE.maladie;
  if (type.includes("conge") || type.includes("rtt"))      return NATURE_PALETTE.conges;
  if (type.includes("sav") || type.includes("reparation") || type.includes("depann") || type.includes("maintenance")) return NATURE_PALETTE.sav;

  return NATURE_PALETTE.sav;
}

// Retourne l'emoji du pack (type_pack prioritaire, puis modele_pv existant)
function getPackEmoji(int) {
  const pack = (int.type_pack || int.modele_pv || "").toLowerCase().replace(/[\s-]/g,"_");
  return PACK_EMOJI[pack] || "";
}

function buildTitle(int) {
  const emoji = getPackEmoji(int);
  return emoji ? `${emoji} ${int.titre}` : int.titre;
}

const LEGENDE_COULEURS = [
  { bg:"#e0f2fe", bd:"#7dd3fc", tx:"#075985", label:"Installation" },
  { bg:"#fef3c7", bd:"#fcd34d", tx:"#92400e", label:"FDI"          },
  { bg:"#f3e8ff", bd:"#d8b4fe", tx:"#6b21a8", label:"SAV"          },
  { bg:"#991b1b", bd:"#450a0a", tx:"#ffffff", label:"Absence / Maladie" },
  { bg:"#d1fae5", bd:"#6ee7b7", tx:"#065f46", label:"Congés / RTT" },
];

const LEGENDE_PACKS = [
  ["🚨","Flash Contrôle"],
  ["🎥","Sentinelle"],
  ["🛠️","SAV Classique"],
  ["💰","Cash Bosch"],
  ["❤️","Defi Care"],
  ["⚡","Défibrillateur"],
];

const TECH_COLORS = [
  "#3b82f6","#10b981","#f59e0b","#8b5cf6","#ef4444",
  "#06b6d4","#84cc16","#f43f5e","#a855f7","#14b8a6"
];
const _tcMap = {}; let _tcIdx = 0;
function techColor(name) {
  if (!name) return "#94a3b8";
  if (!_tcMap[name]) _tcMap[name] = TECH_COLORS[_tcIdx++ % TECH_COLORS.length];
  return _tcMap[name];
}

function getMonday(d) {
  const dt = new Date(d);
  const day = dt.getDay();
  const diff = dt.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(dt.setDate(diff));
}
function sameDay(d1,d2) {
  const a=new Date(d1); a.setHours(0,0,0,0);
  const b=new Date(d2); b.setHours(0,0,0,0);
  return a.getTime()===b.getTime();
}
function isToday(d) {
  const t=new Date(); t.setHours(0,0,0,0);
  const dt=new Date(d); dt.setHours(0,0,0,0);
  return dt.getTime()===t.getTime();
}
function fmtTime(d) {
  if(!d) return "";
  const dt=new Date(d);
  return `${String(dt.getHours()).padStart(2,"0")}:${String(dt.getMinutes()).padStart(2,"0")}`;
}
function getHour(int) {
  const d=int.date_planifiee||int.date_intervention;
  return d ? new Date(d).getHours() : 9;
}

// ── Carte événement ──
function EventCard({ int, onClick, compact=false, T }) {
  const p   = getNaturePalette(int);
  const tc  = techColor(int.technicien_nom);
  const isDark = p.tx === "#ffffff";

  return (
    <div
      draggable
      onDragStart={e => {
        e.dataTransfer.setData("intId", String(int.id));
        e.dataTransfer.setData("intDate", int.date_planifiee || "");
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={e => { e.stopPropagation(); onClick?.(int); }}
      title={int.eta_route_retard?"🚦 Risque de retard : trafic dense sur le trajet depuis l'intervention précédente":undefined}
      style={{
        background: p.bg,
        border: int.eta_route_retard ? "1.5px solid #ef4444" : `1.5px solid ${p.bd}`,
        borderLeft: int.eta_route_retard ? "3px solid #ef4444" : `3px solid ${p.bd}`,
        borderRadius:9,
        padding: compact ? "3px 8px" : "6px 10px",
        marginBottom:3, cursor:"pointer", userSelect:"none",
        transition:"filter .12s,transform .12s",
        position:"relative",
      }}
      onMouseEnter={e => { e.currentTarget.style.filter="brightness(.94)"; e.currentTarget.style.transform="translateY(-1px)"; }}
      onMouseLeave={e => { e.currentTarget.style.filter="none"; e.currentTarget.style.transform="none"; }}>
      {int.eta_route_retard&&(
        <span style={{position:"absolute",top:-6,right:-6,fontSize:13,background:"#ef4444",color:"#fff",borderRadius:"50%",width:18,height:18,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 1px 4px rgba(0,0,0,.3)"}}>⚠️</span>
      )}
      {!compact && (
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
          <span style={{ fontSize:9, fontWeight:700, color:p.tx, background:isDark?"#ffffff22":`${p.bd}55`, padding:"1px 7px", borderRadius:20 }}>
            {p.lb}
          </span>
          <span style={{ fontSize:9, color:isDark?"#fca5a5":"#94a3b8", fontWeight:600 }}>{fmtTime(int.date_planifiee)}</span>
        </div>
      )}
      <div style={{ fontSize:compact?10:12, fontWeight:700, color:isDark?"#ffffff":"#1e293b", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
        {buildTitle(int)}
      </div>
      {!compact && int.client_nom && (
        <div style={{ fontSize:10, color:isDark?"#fecaca":"#64748b", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          👤 {int.client_nom}
        </div>
      )}
      {!compact && int.technicien_nom && (
        <div style={{ fontSize:10, color:isDark?"#fde68a":tc, marginTop:1, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          🔧 {int.technicien_nom}
        </div>
      )}
    </div>
  );
}

// ── Cellule droppable ──
function DropCell({ day, hour, children, onDrop, style={} }) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); onDrop(e, day, hour); }}
      style={{ ...style, background:over?"rgba(99,102,241,.08)":"transparent", transition:"background .1s" }}>
      {children}
    </div>
  );
}

export default function Planning({ T, interventions=[], onSelectInt, onNewInt }) {
  const [view,    setView]    = useState("semaine");
  const [current, setCurrent] = useState(new Date());
  const [selTechs, setSelTechs] = useState(new Set(["all"]));
  const [sideOpen, setSideOpen] = useState(true);
  const [toast, setToast] = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(""),2800); };

  const monday   = useMemo(()=>getMonday(current),[current]);
  const weekDays = useMemo(()=>Array.from({length:7},(_,i)=>{
    const d=new Date(monday); d.setDate(monday.getDate()+i); return d;
  }),[monday]);

  const miniCal = useMemo(()=>{
    const y=current.getFullYear(), m=current.getMonth();
    const first=new Date(y,m,1);
    const startDay=first.getDay()===0?6:first.getDay()-1;
    const days=[];
    for(let i=0;i<startDay;i++) days.push({date:new Date(y,m,1-startDay+i),other:true});
    for(let i=1;i<=new Date(y,m+1,0).getDate();i++) days.push({date:new Date(y,m,i),other:false});
    while(days.length%7!==0){const l=days[days.length-1].date;days.push({date:new Date(l.getFullYear(),l.getMonth(),l.getDate()+1),other:true});}
    return days;
  },[current]);

  const techs = useMemo(()=>[...new Set(interventions.map(i=>i.technicien_nom).filter(Boolean))],[interventions]);

  const filteredInts = useMemo(()=>{
    if(selTechs.has("all")) return interventions;
    return interventions.filter(i=>selTechs.has(i.technicien_nom));
  },[interventions,selTechs]);

  const getDay = (day) => filteredInts.filter(i=>{
    const d=i.date_planifiee||i.date_intervention;
    return d && sameDay(d,day);
  });

  const toggleTech = (name) => setSelTechs(prev=>{
    const next=new Set(prev);
    if(name==="all") return new Set(["all"]);
    next.delete("all");
    if(next.has(name)) next.delete(name); else next.add(name);
    return next.size===0 ? new Set(["all"]) : next;
  });

  const reschedule = useCallback(async (e, targetDay, targetHour) => {
    const intId = e.dataTransfer.getData("intId");
    const origStr = e.dataTransfer.getData("intDate");
    if (!intId) return;
    const orig = origStr ? new Date(origStr) : new Date();
    const nd = new Date(targetDay);
    nd.setHours(targetHour, orig.getMinutes()||0, 0, 0);
    try {
      await fetch(`${BASE}/interventions/${intId}`, {
        method:"PATCH", headers:hdr(),
        body:JSON.stringify({ date_planifiee:nd.toISOString() }),
      });
      showToast("✅ Intervention replanifiée");
      window.location.reload();
    } catch { showToast("❌ Erreur lors de la replanification"); }
  },[]);

  const nav = {
    prev() { const d=new Date(current); view==="mois"?d.setMonth(d.getMonth()-1):view==="jour"?d.setDate(d.getDate()-1):d.setDate(d.getDate()-7); setCurrent(d); },
    next() { const d=new Date(current); view==="mois"?d.setMonth(d.getMonth()+1):view==="jour"?d.setDate(d.getDate()+1):d.setDate(d.getDate()+7); setCurrent(d); },
  };

  const headerTitle = () => {
    if(view==="mois")     return `${MOIS[current.getMonth()]} ${current.getFullYear()}`;
    if(view==="jour")     return current.toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"});
    if(view==="timeline") return `Semaine du ${monday.getDate()} ${MOIS[monday.getMonth()]}`;
    return `${monday.getDate()} ${MOIS[monday.getMonth()]} — ${weekDays[6].getDate()} ${MOIS[weekDays[6].getMonth()]} ${current.getFullYear()}`;
  };

  const nowPct = () => `${(new Date().getMinutes()/60)*100}%`;

  return (
    <div style={{ display:"flex", height:"calc(100vh - 110px)", overflow:"hidden", borderRadius:16, border:`1px solid ${T.border}`, background:T.card }}>

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", top:24, right:24, zIndex:9999, background:"#1e293b", color:"#fff", padding:"11px 20px", borderRadius:12, fontSize:13, fontWeight:600, boxShadow:"0 8px 24px #00000044", pointerEvents:"none" }}>
          {toast}
        </div>
      )}

      {/* ════════ SIDEBAR ════════ */}
      <div style={{ width:sideOpen?238:50, flexShrink:0, borderRight:`1px solid ${T.border}`, display:"flex", flexDirection:"column", transition:"width .22s cubic-bezier(.4,0,.2,1)", overflow:"hidden", background:T.card }}>

        {/* Toggle */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:sideOpen?"space-between":"center", padding:sideOpen?"14px 14px 10px":"10px 8px", borderBottom:`1px solid ${T.border}`, flexShrink:0 }}>
          {sideOpen && <div style={{ fontSize:13, fontWeight:800, color:T.text }}>📅 Planning</div>}
          <button onClick={()=>setSideOpen(p=>!p)}
            style={{ width:28, height:28, borderRadius:8, border:`1px solid ${T.border}`, background:"transparent", color:T.muted, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, flexShrink:0 }}>
            {sideOpen?"‹":"›"}
          </button>
        </div>

        {sideOpen && (
          <div style={{ flex:1, overflowY:"auto", padding:"12px 12px 20px", display:"flex", flexDirection:"column", gap:14 }}>

            {/* Nouveau */}
            <button onClick={onNewInt}
              style={{ width:"100%", padding:"11px", borderRadius:12, background:`linear-gradient(135deg,${T.accent},${T.purple||"#8b5cf6"})`, border:"none", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
              ＋ Nouvelle intervention
            </button>

            {/* Mini calendrier */}
            <div style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:13, padding:13 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <button onClick={()=>{const d=new Date(current);d.setMonth(d.getMonth()-1);setCurrent(d);}} style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:17, lineHeight:1 }}>‹</button>
                <div style={{ fontSize:12, fontWeight:700, color:T.text }}>{MOIS[current.getMonth()].slice(0,4)} {current.getFullYear()}</div>
                <button onClick={()=>{const d=new Date(current);d.setMonth(d.getMonth()+1);setCurrent(d);}} style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:17, lineHeight:1 }}>›</button>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:1, marginBottom:4 }}>
                {["L","M","M","J","V","S","D"].map((j,i)=><div key={i} style={{ textAlign:"center", fontSize:9, fontWeight:700, color:"#94a3b8" }}>{j}</div>)}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
                {miniCal.map((item,i)=>{
                  const isT=isToday(item.date);
                  const inWk=weekDays.some(w=>sameDay(w,item.date));
                  const hasI=filteredInts.some(n=>{const d=n.date_planifiee||n.date_intervention;return d&&sameDay(d,item.date);});
                  return(
                    <div key={i} onClick={()=>setCurrent(new Date(item.date))}
                      style={{ textAlign:"center", padding:"4px 1px", borderRadius:7, cursor:"pointer", position:"relative",
                        background:isT?T.accent:inWk?T.accentDim:"transparent",
                        color:isT?"#fff":item.other?"#cbd5e1":T.text,
                        fontSize:10, fontWeight:isT?900:inWk?700:400 }}>
                      {item.date.getDate()}
                      {hasI&&!isT&&<div style={{ position:"absolute", bottom:1, left:"50%", transform:"translateX(-50%)", width:3, height:3, borderRadius:"50%", background:T.accent }}/>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Filtre techs */}
            <div style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:13, padding:13 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <div style={{ fontSize:11, fontWeight:700, color:T.text }}>👥 Techniciens</div>
                <button onClick={()=>setSelTechs(new Set(["all"]))} style={{ background:"none", border:"none", color:T.accent, cursor:"pointer", fontSize:10, fontWeight:600 }}>Tous</button>
              </div>
              {[["all","Tous",interventions.length],...techs.map(t=>[t,t,interventions.filter(i=>i.technicien_nom===t).length])].map(([val,lbl,cnt])=>{
                const col=val==="all"?T.accent:techColor(val);
                const sel=selTechs.has(val);
                return(
                  <div key={val} onClick={()=>toggleTech(val)}
                    style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 8px", borderRadius:8, cursor:"pointer", background:sel?`${col}18`:"transparent", marginBottom:3 }}>
                    <div style={{ width:15, height:15, borderRadius:4, border:`2px solid ${sel?col:T.border}`, background:sel?col:"transparent", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                      {sel&&<svg width="8" height="6" viewBox="0 0 8 6" fill="none"><path d="M1 2.5L3 4.5L7 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/></svg>}
                    </div>
                    {val!=="all"&&<div style={{ width:7, height:7, borderRadius:"50%", background:col, flexShrink:0 }}/>}
                    <span style={{ fontSize:12, color:sel?col:T.text, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontWeight:sel?700:400 }}>{lbl}</span>
                    <span style={{ fontSize:10, color:T.muted }}>{cnt}</span>
                  </div>
                );
              })}
            </div>

            {/* Légende */}
            <div style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:13, padding:13 }}>
              <div style={{ fontSize:11, fontWeight:700, color:T.text, marginBottom:9 }}>Couleurs</div>
              {LEGENDE_COULEURS.map(({bg,bd,label})=>(
                <div key={label} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                  <div style={{ width:20, height:13, borderRadius:3, background:bg, border:`1.5px solid ${bd}`, flexShrink:0 }}/>
                  <span style={{ fontSize:11, color:T.text }}>{label}</span>
                </div>
              ))}
              <div style={{ marginTop:9, paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <div style={{ fontSize:11, fontWeight:700, color:T.text, marginBottom:7 }}>Packs</div>
                {LEGENDE_PACKS.map(([emoji,label])=>(
                  <div key={label} style={{ display:"flex", alignItems:"center", gap:7, marginBottom:5 }}>
                    <span style={{ fontSize:13 }}>{emoji}</span>
                    <span style={{ fontSize:11, color:T.muted }}>{label}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:8, paddingTop:7, borderTop:`1px solid ${T.border}`, fontSize:10, color:T.muted, fontStyle:"italic" }}>
                💡 Glisser-déposer pour replanifier
              </div>
            </div>

          </div>
        )}
      </div>

      {/* ════════ CALENDRIER PRINCIPAL ════════ */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", minWidth:0 }}>

        {/* ── Header ── */}
        <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 16px", borderBottom:`1px solid ${T.border}`, flexShrink:0, background:T.card, flexWrap:"wrap" }}>
          <button onClick={()=>setCurrent(new Date())}
            style={{ padding:"7px 14px", borderRadius:9, border:`1px solid ${T.border}`, background:"transparent", color:T.text, fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
            Aujourd'hui
          </button>
          <div style={{ display:"flex", gap:3 }}>
            <button onClick={nav.prev} style={{ width:30, height:30, borderRadius:8, border:`1px solid ${T.border}`, background:"transparent", color:T.muted, cursor:"pointer", fontSize:17, display:"flex", alignItems:"center", justifyContent:"center" }}>‹</button>
            <button onClick={nav.next} style={{ width:30, height:30, borderRadius:8, border:`1px solid ${T.border}`, background:"transparent", color:T.muted, cursor:"pointer", fontSize:17, display:"flex", alignItems:"center", justifyContent:"center" }}>›</button>
          </div>
          <div style={{ fontSize:14, fontWeight:700, color:T.text, flex:1, textTransform:"capitalize", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {headerTitle()}
          </div>
          <div style={{ display:"flex", background:T.bg, border:`1px solid ${T.border}`, borderRadius:10, overflow:"hidden", flexShrink:0 }}>
            {[["jour","Jour"],["semaine","Semaine"],["mois","Mois"],["timeline","Timeline"],["liste","Liste"]].map(([k,l])=>(
              <button key={k} onClick={()=>setView(k)}
                style={{ padding:"7px 13px", background:view===k?T.accentDim:"transparent", border:"none", borderRight:`1px solid ${T.border}`, color:view===k?T.accent:"#64748b", fontSize:12, fontWeight:view===k?700:500, cursor:"pointer", transition:"background .12s", whiteSpace:"nowrap" }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* ════════ VUE SEMAINE ════════ */}
        {view==="semaine"&&(
          <div style={{ flex:1, overflowY:"auto", overflowX:"auto" }}>
            <div style={{ display:"grid", gridTemplateColumns:"52px repeat(7,minmax(110px,1fr))", borderBottom:`1px solid ${T.border}`, position:"sticky", top:0, background:T.card, zIndex:2 }}>
              <div/>
              {weekDays.map((day,i)=>{
                const isT=isToday(day);
                const cnt=getDay(day).length;
                return(
                  <div key={i} style={{ padding:"10px 8px", textAlign:"center", borderLeft:`1px solid ${T.border}22` }}>
                    <div style={{ fontSize:10, fontWeight:600, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".5px" }}>{JOURS[i]}</div>
                    <div style={{ width:34, height:34, borderRadius:"50%", background:isT?T.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center", margin:"4px auto 2px", fontSize:17, fontWeight:800, color:isT?"#fff":T.text }}>
                      {day.getDate()}
                    </div>
                    {cnt>0&&<div style={{ fontSize:10, color:T.accent, fontWeight:700 }}>{cnt} ✦</div>}
                  </div>
                );
              })}
            </div>
            {HEURES.map(hr=>(
              <div key={hr} style={{ display:"grid", gridTemplateColumns:"52px repeat(7,minmax(110px,1fr))", minHeight:72, borderBottom:`1px solid #f1f5f9` }}>
                <div style={{ padding:"6px 8px 0", fontSize:10, color:"#94a3b8", textAlign:"right", fontWeight:500 }}>{hr}:00</div>
                {weekDays.map((day,di)=>{
                  const dayInts=getDay(day).filter(i=>getHour(i)===hr);
                  const isT=isToday(day);
                  return(
                    <DropCell key={di} day={day} hour={hr} onDrop={reschedule}
                      style={{ borderLeft:`1px solid #f1f5f9`, padding:"3px 5px", minHeight:72, background:isT?`${T.accent}04`:"transparent", position:"relative" }}>
                      {isT&&new Date().getHours()===hr&&(
                        <div style={{ position:"absolute", top:nowPct(), left:0, right:0, height:2, background:"#ef4444", zIndex:1 }}>
                          <div style={{ position:"absolute", left:-4, top:-4, width:9, height:9, borderRadius:"50%", background:"#ef4444" }}/>
                        </div>
                      )}
                      {dayInts.map(int=><EventCard key={int.id} int={int} onClick={onSelectInt} T={T}/>)}
                      {dayInts.length===0&&(
                        <div onClick={()=>onNewInt?.()} style={{ height:"100%", minHeight:64, cursor:"pointer", borderRadius:7 }}
                          onMouseEnter={e=>e.currentTarget.style.background=`${T.accent}10`}
                          onMouseLeave={e=>e.currentTarget.style.background="transparent"}/>
                      )}
                    </DropCell>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* ════════ VUE JOUR ════════ */}
        {view==="jour"&&(
          <div style={{ flex:1, overflowY:"auto" }}>
            <div style={{ position:"sticky", top:0, background:T.card, borderBottom:`1px solid ${T.border}`, zIndex:2, padding:"12px 20px", display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:46, height:46, borderRadius:"50%", background:isToday(current)?T.accent:T.accentDim, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:9, fontWeight:700, color:isToday(current)?"#fff":T.accent, lineHeight:1 }}>{JOURS[(current.getDay()+6)%7]}</div>
                  <div style={{ fontSize:18, fontWeight:900, color:isToday(current)?"#fff":T.accent, lineHeight:1 }}>{current.getDate()}</div>
                </div>
              </div>
              <div style={{ fontSize:14, fontWeight:700, color:T.text, textTransform:"capitalize" }}>
                {current.toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
              </div>
              <div style={{ fontSize:13, color:T.muted, marginLeft:"auto" }}>
                {getDay(current).length} intervention{getDay(current).length!==1?"s":""}
              </div>
            </div>
            {HEURES.map(hr=>{
              const dayInts=filteredInts.filter(i=>{
                const d=i.date_planifiee||i.date_intervention;
                return d && sameDay(d,current) && new Date(d).getHours()===hr;
              });
              return(
                <div key={hr} style={{ display:"grid", gridTemplateColumns:"56px 1fr" }}>
                  <div style={{ padding:"8px 10px 0", fontSize:10, color:"#94a3b8", textAlign:"right", borderBottom:`1px solid #f1f5f9`, minHeight:80, fontWeight:500 }}>{hr}:00</div>
                  <DropCell day={current} hour={hr} onDrop={reschedule}
                    style={{ padding:"5px 16px", borderBottom:`1px solid #f1f5f9`, minHeight:80, position:"relative", borderLeft:`1px solid #f1f5f9` }}>
                    {isToday(current)&&new Date().getHours()===hr&&(
                      <div style={{ position:"absolute", top:nowPct(), left:0, right:0, height:2, background:"#ef4444" }}>
                        <div style={{ position:"absolute", left:-4, top:-4, width:9, height:9, borderRadius:"50%", background:"#ef4444" }}/>
                      </div>
                    )}
                    {dayInts.map(int=><EventCard key={int.id} int={int} onClick={onSelectInt} T={T}/>)}
                  </DropCell>
                </div>
              );
            })}
          </div>
        )}

        {/* ════════ VUE MOIS ════════ */}
        {view==="mois"&&(
          <div style={{ flex:1, overflowY:"auto" }}>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", borderBottom:`1px solid ${T.border}`, position:"sticky", top:0, background:T.card, zIndex:2 }}>
              {JOURS.map(j=><div key={j} style={{ padding:"10px 8px", textAlign:"center", fontSize:10, fontWeight:700, color:"#94a3b8", textTransform:"uppercase", letterSpacing:".5px" }}>{j}</div>)}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)" }}>
              {miniCal.map((item,i)=>{
                const isT=isToday(item.date);
                const dayInts=getDay(item.date);
                return(
                  <DropCell key={i} day={item.date} hour={9} onDrop={reschedule}
                    style={{ minHeight:108, borderRight:`1px solid #f1f5f9`, borderBottom:`1px solid #f1f5f9`, padding:"6px", background:isT?`${T.accent}06`:"transparent" }}>
                    <div style={{ width:26, height:26, borderRadius:"50%", background:isT?T.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, color:isT?"#fff":item.other?"#cbd5e1":T.text, marginBottom:4 }}>
                      {item.date.getDate()}
                    </div>
                    {dayInts.slice(0,3).map(int=><EventCard key={int.id} int={int} onClick={onSelectInt} T={T} compact/>)}
                    {dayInts.length>3&&<div style={{ fontSize:9, color:T.accent, fontWeight:700, paddingLeft:3 }}>+{dayInts.length-3} autres</div>}
                  </DropCell>
                );
              })}
            </div>
          </div>
        )}

        {/* ════════ VUE TIMELINE (par technicien) ════════ */}
        {view==="timeline"&&(
          <div style={{ flex:1, overflowY:"auto", overflowX:"auto" }}>
            <div style={{ display:"grid", gridTemplateColumns:`160px repeat(${HEURES.length*7},60px)`, borderBottom:`1px solid ${T.border}`, position:"sticky", top:0, background:T.card, zIndex:2, minWidth:"max-content" }}>
              <div style={{ padding:"8px 12px", fontSize:10, fontWeight:700, color:"#94a3b8", textTransform:"uppercase" }}>Technicien</div>
              {weekDays.map((day,di)=>(
                HEURES.map(hr=>(
                  <div key={`${di}-${hr}`} style={{ padding:"6px 2px", textAlign:"center", borderLeft: hr===7?`1px solid ${T.border}22`:"none", fontSize:9, color: sameDay(day,new Date())&&hr===new Date().getHours()?"#ef4444":"#94a3b8", fontWeight:600 }}>
                    {hr===7?JOURS[di]:hr===13?`${hr}h`:""}
                  </div>
                ))
              ))}
            </div>

            {(selTechs.has("all")?techs:[...selTechs]).map(techName=>{
              const col=techColor(techName);
              const techInts=filteredInts.filter(i=>i.technicien_nom===techName);
              return(
                <div key={techName} style={{ display:"grid", gridTemplateColumns:`160px repeat(${HEURES.length*7},60px)`, borderBottom:`1px solid #f1f5f9`, minHeight:68, minWidth:"max-content" }}>
                  <div style={{ padding:"8px 12px", display:"flex", alignItems:"center", gap:9, borderRight:`1px solid ${T.border}`, background:T.bg, position:"sticky", left:0, zIndex:1 }}>
                    <div style={{ width:30, height:30, borderRadius:"50%", background:`${col}22`, border:`2px solid ${col}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:col, flexShrink:0 }}>
                      {(techName||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
                    </div>
                    <div style={{ fontSize:11, fontWeight:600, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{techName}</div>
                  </div>
                  {weekDays.map((day,di)=>(
                    HEURES.map(hr=>{
                      const hInts=techInts.filter(i=>{const d=i.date_planifiee||i.date_intervention;return d&&sameDay(d,day)&&new Date(d).getHours()===hr;});
                      return(
                        <DropCell key={`${di}-${hr}`} day={day} hour={hr} onDrop={reschedule}
                          style={{ borderLeft: hr===7?`1px solid ${T.border}22`:"none", padding:"3px 2px", minHeight:68, position:"relative" }}>
                          {sameDay(day,new Date())&&new Date().getHours()===hr&&(
                            <div style={{ position:"absolute", top:nowPct(), left:0, right:0, height:2, background:"#ef444466", zIndex:1 }}/>
                          )}
                          {hInts.map(int=>{
                            const p=getNaturePalette(int);
                            const emoji=getPackEmoji(int);
                            return(
                              <div key={int.id} draggable
                                onDragStart={e=>{e.dataTransfer.setData("intId",String(int.id));e.dataTransfer.setData("intDate",int.date_planifiee||"");}}
                                onClick={e=>{e.stopPropagation();onSelectInt?.(int);}}
                                style={{ background:p.bg, border:`1.5px solid ${p.bd}`, borderRadius:7, padding:"3px 5px", marginBottom:2, cursor:"pointer", fontSize:9, overflow:"hidden" }}>
                                <div style={{ fontWeight:700, color:p.tx, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                  {emoji}{emoji?" ":""}{int.client_nom||int.titre}
                                </div>
                              </div>
                            );
                          })}
                        </DropCell>
                      );
                    })
                  ))}
                </div>
              );
            })}
            {techs.length===0&&(
              <div style={{ padding:"48px", textAlign:"center", color:"#94a3b8", fontSize:13 }}>
                <div style={{ fontSize:36, marginBottom:10 }}>🗓</div>
                Aucun technicien avec des interventions planifiées
              </div>
            )}
          </div>
        )}

        {/* ════════ VUE LISTE ════════ */}
        {view==="liste"&&(
          <div style={{ flex:1, overflowY:"auto", padding:"16px 20px" }}>
            {Array.from({length:14},(_,i)=>{
              const d=new Date(monday);
              d.setDate(monday.getDate()+i);
              const ints=getDay(d);
              const isT=isToday(d);
              return(
                <div key={i} style={{ marginBottom:18 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:8 }}>
                    <div style={{ width:40, height:40, borderRadius:"50%", background:isT?T.accent:T.accentDim, border:`2px solid ${isT?T.accent:T.border}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <div style={{ textAlign:"center" }}>
                        <div style={{ fontSize:8, fontWeight:700, color:isT?"#fff":T.accent, lineHeight:1 }}>{JOURS[(d.getDay()+6)%7]}</div>
                        <div style={{ fontSize:16, fontWeight:900, color:isT?"#fff":T.accent, lineHeight:1 }}>{d.getDate()}</div>
                      </div>
                    </div>
                    <div style={{ flex:1, height:1, background:`${T.border}66` }}/>
                    {ints.length>0&&<span style={{ fontSize:11, color:T.accent, fontWeight:700, flexShrink:0 }}>{ints.length} int.</span>}
                  </div>
                  {ints.length===0
                    ? <div style={{ paddingLeft:52, fontSize:12, color:T.muted, fontStyle:"italic" }}>Aucune intervention</div>
                    : <div style={{ display:"flex", flexDirection:"column", gap:6, paddingLeft:52 }}>
                        {ints.map(int=>{
                          const p=getNaturePalette(int);
                          const emoji=getPackEmoji(int);
                          return(
                            <div key={int.id} onClick={()=>onSelectInt?.(int)}
                              style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 16px", background:T.card, border:`1.5px solid ${T.border}`, borderLeft:`4px solid ${p.bd}`, borderRadius:"0 11px 11px 0", cursor:"pointer", transition:"all .15s" }}
                              onMouseEnter={e=>e.currentTarget.style.background=T.accentDim}
                              onMouseLeave={e=>e.currentTarget.style.background=T.card}>
                              <div style={{ fontSize:12, fontWeight:700, color:T.muted, minWidth:36, flexShrink:0 }}>{fmtTime(int.date_planifiee||int.date_intervention)}</div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{emoji}{emoji?" ":""}{int.titre}</div>
                                <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>👤 {int.client_nom||"—"} · 🔧 {int.technicien_nom||"Non assigné"}</div>
                              </div>
                              <span style={{ fontSize:10, fontWeight:700, color:p.tx, background:p.bg, border:`1px solid ${p.bd}`, padding:"3px 10px", borderRadius:20, whiteSpace:"nowrap", flexShrink:0 }}>{p.lb}</span>
                            </div>
                          );
                        })}
                      </div>
                  }
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}
